/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Button, IModuleProps, INodeProps, NodeTag } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observable } from 'mobx';
import * as React from 'react';
import { IDXC_GetStockistsServiceResponse } from '../../data-actions/generated/DataServiceEntities.g';
// import { getStockcistsCityorStateAction, GetStockistsCityOrStateInput } from '../../data-actions/stockists/stockists-cityorstate-data';
// import { getStockcistsCountryRegionsAction, GetStockistsCountryRegionsInput } from '../../data-actions/stockists/stockists-country-data';
import { getStockcistsStoreAction, GetStockistsStoreInput } from '../../data-actions/stockists/stockists-store';
// import { IDXC_CountryView } from '../../modal/stockistsListTable';
import { ICittaStockistsData } from './citta-stockists.data';
import { ICittaStockistsProps } from './citta-stockists.props.autogenerated';

export interface ICittaStockistsState {
    countrySelected: string;
    stateorcitySelected: string;
    searchString: string;
    countryList: [];
    stateList: [];
    stockistList: [];
}

export interface ICittaStockistsViewProps extends ICittaStockistsProps<ICittaStockistsData> {
    stockistMain: INodeProps;
    stockistGrid: INodeProps;
    StockistContainer: IModuleProps;
    inputWrap: INodeProps;
    stockistTop: INodeProps;
    inputThirds: INodeProps;
    createStockistsAddress: React.ReactNode;
    createCountryDropDown: React.ReactNode;
    createCityStateDropDown: React.ReactNode;
    createSearchAddress: React.ReactNode;
}

/**
 *
 * CittaStockists component
 * @extends {React.PureComponent<ICittaStockistsProps<ICittaStockistsData>>}
 */
class CittaStockists extends React.PureComponent<ICittaStockistsProps<ICittaStockistsData>> {
    public state: ICittaStockistsState = {
        countrySelected: '',
        stateorcitySelected: '',
        searchString: '',
        countryList: [],
        stateList: [],
        stockistList: []
    };

    @observable private _countryList: string[] = [];
    @observable private _stateList: string[] = [];
    @observable private _stockistList: IDXC_GetStockistsServiceResponse | undefined;

    private _countrySelectRef: React.RefObject<HTMLSelectElement>;
    private _stateorcitySelectRef: React.RefObject<HTMLSelectElement>;
    private _searchInputRef: React.RefObject<HTMLInputElement>;

    public constructor(props: ICittaStockistsProps<ICittaStockistsData>) {
        super(props);
        this._countrySelectRef = React.createRef();
        this._stateorcitySelectRef = React.createRef();
        this._searchInputRef = React.createRef();
    }

    public async componentDidMount(): Promise<void> {
        this._getCountryList().catch(err => console.log(err));
        this._getStateProvinces('').catch(err => console.log(err));
        this._getStockistsList().catch(err => console.log(err));
    }
    public render(): JSX.Element {
        const viewProps = {
            ...this.props,
            StockistContainer: {
                moduleProps: this.props,
                className: classnames('infoSection')
            },
            stockistTop: {
                tag: 'div' as NodeTag,
                className: classnames('stockistTop', 'inputThirds')
            },
            stockistMain: {
                tag: 'div' as NodeTag,
                className: classnames('stockistMain')
            },
            // stockistGrid: {
            //     tag: 'div' as NodeTag,
            //     className: classnames('stockistGrid')
            // },
            createCityStateDropDown: this._renderCityStateDropDown(),
            createCountryDropDown: this._renderCountryDropDown(),
            createStockistsAddress: this._renderStockistsAddress(),
            createSearchAddress: this._renderSearchAddress(),
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }
    private _renderSearchAddress(): React.ReactNode {
        const searchPlaceholder = this.props && this.props.resources && this.props.resources.searchPlaceholder ? this.props.resources.searchPlaceholder : 'Search by Address';
        return (
            <form className='stockists-form inputWrap inputFW addressSearch' >
                <div className='searchText'>
                    <div className='c-defInput'>
                        <input
                            ref={this._searchInputRef}
                            type='text'
                            aria-label='Address Search'
                            autoComplete='off'
                            className='addressSearch'
                            placeholder={searchPlaceholder}
                            onChange={this._changeSearchText(this._searchInputRef)}
                        />
                    </div>
                </div>
                <div className='stockistsearch-submit'>
                    <Button
                        className='stockistsearch-submit-button'
                        title={'Search'}
                        color='primary'
                        onClick={this._handleSearch}
                        aria-label='Search'
                    />
                </div>
            </form>
        );
    }
    private _handleSearch = (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        const searchStr = (this._searchInputRef && this._searchInputRef.current && this._searchInputRef.current.value) ? this._searchInputRef.current.value : '';
        const countryValue = (this._countrySelectRef && this._countrySelectRef.current && this._countrySelectRef.current.value) ? this._countrySelectRef.current.value : '';
        const stateValue = (this._stateorcitySelectRef && this._stateorcitySelectRef.current && this._stateorcitySelectRef.current.value) ? this._stateorcitySelectRef.current.value : '';
        try {
            this._getStockistsList(countryValue, stateValue, searchStr).catch(err => console.log(err));
        } catch (error) {
            console.log(error);
        }
    }
    private _changeSearchText = (ref: React.RefObject<HTMLInputElement>) => (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState(
            { searchString: e.target.value }
        );
    }
    private _renderCityStateDropDown(): React.ReactNode {
        const citaAndStatePlaceholder = this.props && this.props.resources && this.props.resources.citaAndStatePlaceholder ? this.props.resources.citaAndStatePlaceholder : 'All Cities/States';
        return (
            <div className='inputWrap'>
                <div className='c-defSelWrap'>
                    <select
                        ref={this._stateorcitySelectRef}
                        className='country-picker'
                        aria-label='State or City'
                        name='stateorcity'
                        id='stateorcityselect'
                        value={this.state.stateorcitySelected}
                        onChange={this._onStateorCityChange}
                    >
                        <option value='' selected={true} aria-selected={true}>{citaAndStatePlaceholder}</option>
                        {this._renderStateorCityList()}
                    </select>
                </div>
            </div>
        );
    }
    private _renderCountryDropDown(): React.ReactNode {
        const countryPlaceholder = this.props && this.props.resources && this.props.resources.countryPlaceholder ? this.props.resources.countryPlaceholder : 'All Country';
        return (
            <div className='inputWrap'>
                <div className='c-defSelWrap'>
                    <select
                        ref={this._countrySelectRef}
                        className='country-picker'
                        aria-label='Countries'
                        name='countries'
                        id='countryselect'
                        value={this.state.countrySelected}
                        onChange={this._onCountryChange}
                    >
                        <option value='' selected={true} aria-selected={true}>{countryPlaceholder}</option>
                        {this._renderCountryList()}
                    </select>
                </div>
            </div>
        );
    }
    private _onStateorCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ stateorcitySelected: e.target.value || '' });
        this._getStockistsList(this.state.countrySelected, e.target.value).catch(err => console.log(err));
    }
    private _renderStateorCityList(): JSX.Element[] | null {
        const stateorcity = this.state.stateList;
        if (stateorcity) {
            return stateorcity.map((stateorcityItem) =>
                this._getCities(stateorcityItem)
            );
        } else {
            return null;
        }
    }
    private _getCities(stateorcityItem: string): JSX.Element {
        return <option value={stateorcityItem} selected={false} aria-selected={false}>{stateorcityItem}</option>;
    }
    private _renderCountryList(): JSX.Element[] | null {
        const country = this.state.countryList;
        if (country) {
            return country.map((countryItem: string) =>
                this._getCountries(countryItem)
            );
        } else {
            return null;
        }
    }
    private _getCountries(countryItem: string): JSX.Element {
        return <option value={countryItem} selected={false} aria-selected={false}>{countryItem}</option>;
    }
    private _onCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ countrySelected: e.target.value || '' });
        this._getStateProvinces(e.target.value).catch(err => console.log(err));
        this._getStockistsList(e.target.value).catch(err => console.log(err));
    }
    private _renderStockistsAddressBlock(stockistList: any): React.ReactNode {

        if (stockistList) {
            return stockistList.map((stockist: any) =>
            (


                <div className='col-auto col-sm-4 col-md-4 col-lg-4'>
                    <div className='ms-content-block citta-content-placement-item-11 font-light ms-content-block__details'>
                        <h5 className='font-light'>{stockist.Store}</h5>
                    
                        <p>
                            {stockist.StreetNumber} {stockist.Street}
                            {stockist.Suburb ? <div>{`${stockist.Suburb}`}</div> : ''}
                            {stockist.City} {stockist.PostalCode} <br />
                            {stockist.State} {stockist.CountryName} <br />
                        </p>
                
                    </div>
                    
                </div>


            )
            );
        } else {
            return null;
        }
    }

    private _renderStockistsAddress(): React.ReactNode {
         const lists = [
       { name: 'New Zealand', items: this.props.config.stockistsAddressNZ },
        { name: 'Australia',  items: this.props.config.stockistsAddressAU },
        { name: 'International', items: this.props.config.stockistsAddressInt }
         
    ];

    return (
        <>
            {lists.map((list, idx) => (
                <div className="col-12" key={idx}>
                    <div className='title'><span>{list.name}</span></div>
                    <div className="default-container-3-slots default-container-3-slots__3 fluid text-no-content-row">
                        {this._renderStockistsAddressBlock(list.items)}
                    </div>
                </div>
            ))}
        </>
    );
    }


    private async _getCountryList(): Promise<void> {
        let list = ["New Zealand", "Australia"];
        this.props.config.stockistsAddressInt?.map((item: any) => {
            if (!list.includes(item.CountryName)) {
                list.push(item.CountryName);
            }
        });

         this._countryList = list;

      if (this._countryList) {
                    this.setState({
                        countryList: this._countryList
                    });
                }
    }

    private async _getStateProvinces(countryCode: string): Promise<void> {
        let list: string[] = [];
        let listNZ: string[] = [], listAU: string[] = [], listInt: string[] = [];
        this.props.config.stockistsAddressNZ?.map((item: any) => {
            console.log('item', item);
            if (!listNZ?.includes(item.City)) {
                console.log('item.State', item.State);
                listNZ?.push(item.City);
            }                       
        });
        this.props.config.stockistsAddressAU?.map((item: any) => {
            if (!listAU?.includes(item.State)) {
                listAU?.push(item.State);
            }                       
        }); 
        this.props.config.stockistsAddressInt?.map((item: any) => {
            if (!listInt?.includes(item.State)) {
                listInt?.push(item.State);
            }   
        });
        if (countryCode === 'New Zealand') {
            console.log('listNZ', listNZ);
            list = listNZ;
        }               
        else if (countryCode === 'Australia') {
            list = listAU;
        }
        else if (countryCode==="United States") {
            list = listInt;
        }
        
        this._stateList =  list ;
        if (this._stateList) {
            this.setState({
                stateList: this._stateList
            });
        }
    }

    private async _getStockistsList(countryCode?: string, cityorstate?: string, searchStr?: string): Promise<void> {
        let response: IDXC_GetStockistsServiceResponse;
        if (this.props.context && this.props.context.actionContext) {
            try {
                const input = new GetStockistsStoreInput(countryCode, cityorstate, searchStr);
                response = await getStockcistsStoreAction(input, this.props.context.actionContext);
                this._stockistList = response;
                if (this._stockistList) {
                    this.setState({
                        stockistList: this._stockistList
                    });
                }
            } catch (error) {
                console.log(error);
            }
        }
    }
}
export default CittaStockists;
